import React, { useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { loadLogoForPDF, addLogoToPDF } from '../pdfLogoUtil';

const TreatmentPlanReviewPDF = ({ chartData, chartRef, user }) => {
  const [generating, setGenerating] = useState(false);

  const generatePDF = async () => {
    setGenerating(true);

    try {
      const pdf = new jsPDF('l', 'mm', 'letter'); // Start landscape for chart
      const userName = user?.name || 'Unknown User';
      const timestamp = new Date().toLocaleString();

      const logoDataUrl = await loadLogoForPDF();
      const addLogo = (x, yPos, w, h) => {
        addLogoToPDF(pdf, logoDataUrl, x, yPos, w, h);
      };

      // ============================================================
      // PAGE 1: Chart (landscape) with header info at top
      // ============================================================
      const lPageWidth = pdf.internal.pageSize.getWidth();
      const lPageHeight = pdf.internal.pageSize.getHeight();
      const lMargin = 15;
      let y = lMargin;

      // Header row: title left, logo right
      const headerLogoH = 16;
      const headerLogoW = headerLogoH * 3;

      addLogo(lPageWidth - lMargin - headerLogoW, y, headerLogoW, headerLogoH);

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 58, 138);
      pdf.text('Treatment Plan Review Tracker Analysis', lMargin, y + 6);

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      pdf.text(`Prepared by: ${userName}  |  Generated: ${timestamp}`, lMargin, y + 12);
      y += headerLogoH + 4;

      // Divider
      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.3);
      pdf.line(lMargin, y, lPageWidth - lMargin, y);
      y += 3;

      // Chart image
      if (chartRef?.current) {
        try {
          const canvas = await html2canvas(chartRef.current, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true
          });
          const chartImgData = canvas.toDataURL('image/png');

          const legendSpace = 14; // space for manually drawn legend
          const footerReserve = 32; // space for footer
          const availableHeight = lPageHeight - y - footerReserve - legendSpace;
          const availableWidth = lPageWidth - lMargin * 2;

          const aspectRatio = canvas.height / canvas.width;
          let imgWidth = availableWidth;
          let imgHeight = imgWidth * aspectRatio;

          if (imgHeight > availableHeight) {
            imgHeight = availableHeight;
            imgWidth = imgHeight / aspectRatio;
          }

          // Center horizontally
          const imgX = lMargin + (availableWidth - imgWidth) / 2;
          pdf.addImage(chartImgData, 'PNG', imgX, y, imgWidth, imgHeight);
          y += imgHeight + 5;

          // Draw legend manually (avoids html2canvas rendering issues)
          const legendItems = [
            { label: 'Overdue', color: [231, 76, 60] },
            { label: 'Due Soon', color: [243, 156, 18] },
            { label: 'On Time', color: [39, 174, 96] }
          ];
          const boxSize = 6;
          const boxTextGap = 2;
          const itemGap = 18;

          // Pre-calculate total width for centering
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          let totalW = 0;
          legendItems.forEach((item, i) => {
            totalW += boxSize + boxTextGap + pdf.getTextWidth(item.label);
            if (i < legendItems.length - 1) totalW += itemGap;
          });

          let lx = lMargin + (availableWidth - totalW) / 2;
          const ly = y;

          // Reset graphics state before drawing legend
          pdf.setDrawColor(0, 0, 0);
          pdf.setLineWidth(0);

          legendItems.forEach((item, i) => {
            // Draw filled color box (no stroke)
            pdf.setFillColor(item.color[0], item.color[1], item.color[2]);
            pdf.rect(lx, ly - boxSize + 2, boxSize, boxSize, 'F');
            lx += boxSize + boxTextGap;

            // Draw label text in dark color
            pdf.setTextColor(40, 40, 40);
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.text(item.label, lx, ly);
            lx += pdf.getTextWidth(item.label) + itemGap;
          });
        } catch (e) {
          console.error('Chart capture failed:', e);
        }
      }

      // ============================================================
      // PAGE 2+: Analytics (portrait)
      // ============================================================
      pdf.addPage('letter', 'portrait');
      const aPageWidth = pdf.internal.pageSize.getWidth();
      const aPageHeight = pdf.internal.pageSize.getHeight();
      const aMargin = 20;
      y = aMargin;

      // Analytics header with logo
      const aLogoH = 14;
      const aLogoW = aLogoH * 3;
      addLogo(aPageWidth - aMargin - aLogoW, y, aLogoW, aLogoH);

      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 58, 138);
      pdf.text('Treatment Plan Review', aMargin, y + 6);
      pdf.text('Tracker Analysis', aMargin, y + 14);
      y += 20;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      pdf.text(`Prepared by: ${userName}  |  Generated: ${timestamp}`, aMargin, y);
      y += 5;

      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.3);
      pdf.line(aMargin, y, aPageWidth - aMargin, y);
      y += 10;

      const checkPageBreak = (neededSpace) => {
        if (y + neededSpace > aPageHeight - 35) {
          pdf.addPage('letter', 'portrait');
          y = aMargin;
        }
      };

      // Compute analytics
      const total = chartData.length;
      const overdue = chartData.filter(d => d.status === 'Overdue');
      const dueSoon = chartData.filter(d => d.status === 'Due Soon');
      const onTime = chartData.filter(d => d.status === 'On Time');
      const overduePct = (overdue.length / total * 100).toFixed(1);
      const dueSoonPct = (dueSoon.length / total * 100).toFixed(1);
      const complianceScore = ((onTime.length + dueSoon.length) / total * 100).toFixed(1);
      const complianceRating = complianceScore >= 90 ? 'Excellent' : complianceScore >= 75 ? 'Good' : 'Needs Attention';
      const avgInterval = (chartData.reduce((sum, d) => sum + d.targetReviewInterval, 0) / total).toFixed(0);

      // Compliance Status Overview
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 58, 138);
      pdf.text('Compliance Status Overview', aMargin, y);
      y += 9;

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(40, 40, 40);
      const complianceLines = [
        `Total Clients: ${total}`,
        `Overdue Reviews: ${overdue.length} (${overduePct}%)`,
        `Due Soon (Next 14 Days): ${dueSoon.length} (${dueSoonPct}%)`,
        `Overall Compliance Score: ${complianceScore}%`,
        `Compliance Rating: ${complianceRating}`,
        `Average Review Interval: ${avgInterval} days`
      ];

      complianceLines.forEach(line => {
        checkPageBreak(7);
        pdf.text(`\u2022  ${line}`, aMargin + 4, y);
        y += 7;
      });
      y += 10;

      // Action Items
      checkPageBreak(25);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 58, 138);
      pdf.text('Action Items', aMargin, y);
      y += 9;

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(40, 40, 40);
      pdf.text('Overdue:', aMargin + 4, y);
      y += 7;
      pdf.setFont('helvetica', 'normal');

      if (overdue.length > 0) {
        overdue.forEach(d => {
          checkPageBreak(7);
          pdf.text(`\u2022  ${d.clientId}: ${Math.abs(d.daysUntilDue)} days overdue`, aMargin + 8, y);
          y += 7;
        });
      } else {
        pdf.text('\u2022  None', aMargin + 8, y);
        y += 7;
      }
      y += 5;

      checkPageBreak(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Due within 14 days:', aMargin + 4, y);
      y += 7;
      pdf.setFont('helvetica', 'normal');

      if (dueSoon.length > 0) {
        dueSoon.forEach(d => {
          checkPageBreak(7);
          pdf.text(`\u2022  ${d.clientId}: due in ${d.daysUntilDue} days (on ${d.nextReviewDue})`, aMargin + 8, y);
          y += 7;
        });
      } else {
        pdf.text('\u2022  None', aMargin + 8, y);
        y += 7;
      }
      y += 10;

      // Review Status Summary
      checkPageBreak(20);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 58, 138);
      pdf.text('Review Status Summary', aMargin, y);
      y += 9;

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(40, 40, 40);
      if (overdue.length > 0) {
        const mostOverdue = chartData.reduce((min, d) => d.daysUntilDue < min.daysUntilDue ? d : min, chartData[0]);
        pdf.text(`Most Overdue Client: ${mostOverdue.clientId} (${Math.abs(mostOverdue.daysUntilDue)} days overdue)`, aMargin + 4, y);
      } else {
        pdf.text('No overdue reviews.', aMargin + 4, y);
      }

      pdf.save(`Treatment_Plan_Review_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('PDF generation failed: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const hasData = chartData && chartData.length > 0;

  return (
    <button
      onClick={generatePDF}
      disabled={generating || !hasData}
      className={`px-6 py-3 rounded-lg font-semibold transition-all ${
        generating
          ? 'bg-gray-300 text-gray-500 cursor-wait'
          : !hasData
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
          : 'bg-green-600 text-white hover:bg-green-700 shadow-md'
      }`}
    >
      {generating ? 'Generating PDF...' : 'Download PDF'}
    </button>
  );
};

export default TreatmentPlanReviewPDF;
